// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import useHasMounted from '@tcp/core/src/hooks/useHasMounted';
import Image from '@tcp/core/src/components/common/atoms/Image';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import LinkText from '@tcp/core/src/components/common/molecules/LinkText';
import {
  isServer,
  isClient,
  setCookie,
  readCookie,
  getLocator,
  cropImageUrl,
  getDateObject,
  configureInternalNavigationFromCMSUrl,
} from '@tcp/core/src/utils';
import MarqueeSlider from '../molecules/MarqueeSlider';
import CloseButton from '../molecules/CloseButtom';
import CTAItem from '../molecules/CtaItems';
import config, { sliderConfig } from '../config/ModuleSlider.config';
import ModuleSliderStyle, {
  StyledSlider,
  ColorContainer,
  ImageContainer,
  StyledContainer,
  StyledMarqueeContainer,
} from '../styles/ModuleSlider.style';

const CouponCTAContainer = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "CouponCTA Homepage" */ '../molecules/CouponCTA/CouponCTA.container'
  )
);

const SLIDER_POSITION_TOP_NAV = 'topnavwrapper';
const MARQUEE_LEFT_SLIDE = 'marqueeSlideLeft';
const MARQUEE_RIGHT_SLIDE = 'marqueeSlideRight';

const getUUID = (uuidCookieString) => {
  const UUID = readCookie(uuidCookieString);
  return UUID ? UUID.split(',')[0] : '-1002';
};

const renderCloseButton = (
  isPositionTopNav,
  closeHeaderSlider,
  isMultipleSlide,
  showApplyCouponButton
) => {
  return isPositionTopNav ? (
    <CloseButton
      className="slider-close-button"
      closeFunc={closeHeaderSlider}
      isMultipleSlide={isMultipleSlide}
      isCouponButtonVisible={!showApplyCouponButton}
      closeIconDataLocator="slider-close-button"
    />
  ) : (
    <></>
  );
};

const renderTextItem = (headerTextLine, navigation) => {
  return (
    <LinkText
      className="slider-content-innerText"
      headerText={headerTextLine}
      locator="moduleSlider_header_text"
      textAlign="center"
      useStyle
      component="p"
      navigation={navigation}
      noLinkWrap
    />
  );
};

const renderImage = (image) => {
  const { url, alt } = image;
  return (
    <Image
      alt={alt}
      className="marquee-image-item"
      src={cropImageUrl(url)}
      data-locator={getLocator(image?.alt)}
      height="38px"
    />
  );
};

const renderSingleSlide = (
  slider,
  isPositionTopNav,
  closeHeaderSlider,
  isMultipleSlide,
  cartItemsValue,
  isAddCouponHomepageEnabled,
  hasMounted
) => {
  const { headerTextLine, linkedImage, ctaItems } = slider;
  const { color, image, link } = !isEmpty(linkedImage) && linkedImage[0];
  const isOnlyImage = !isEmpty(linkedImage) && !isEmpty(image);
  const showApplyCouponButton = cartItemsValue > 0 && !isOnlyImage;
  let navigationUrl = {};

  if (link) {
    navigationUrl = link;
    navigationUrl.to = configureInternalNavigationFromCMSUrl(link?.url);
    navigationUrl.asPath = link?.url;
  }

  if (isOnlyImage) {
    return (
      <>
        <ImageContainer className="module-slider-image-section">
          <Anchor {...navigationUrl} className="module-slider-background-link">
            {renderImage(image)}
            {!isEmpty(headerTextLine) ? renderTextItem(headerTextLine) : <></>}
          </Anchor>
          {isAddCouponHomepageEnabled && hasMounted && showApplyCouponButton && (
            <CouponCTAContainer headerTextLine={headerTextLine} />
          )}
        </ImageContainer>
        {renderCloseButton(
          isPositionTopNav,
          closeHeaderSlider,
          isMultipleSlide,
          showApplyCouponButton
        )}
      </>
    );
  }

  return (
    <ColorContainer colorData={color} className="module-slider-color-section">
      <Anchor {...navigationUrl} className="module-slider-background-link">
        {!isEmpty(headerTextLine) ? renderTextItem(headerTextLine) : <></>}
      </Anchor>
      {isAddCouponHomepageEnabled && hasMounted && showApplyCouponButton && (
        <CouponCTAContainer headerTextLine={headerTextLine} />
      )}
      {!isEmpty(ctaItems) ? <CTAItem ctaItems={ctaItems} /> : <></>}
      {renderCloseButton(
        isPositionTopNav,
        closeHeaderSlider,
        isMultipleSlide,
        showApplyCouponButton
      )}
    </ColorContainer>
  );
};

const renderSlides = (
  slider,
  isPositionTopNav,
  CAROUSEL_OPTIONS,
  closeHeaderSlider,
  isMultipleSlide,
  cartItemsValue,
  isAddCouponHomepageEnabled,
  hasMounted
) => {
  return (
    <StyledSlider options={CAROUSEL_OPTIONS} carouselConfig={sliderConfig} isModule>
      {slider.map((item) => {
        return (
          !isEmpty(item) &&
          renderSingleSlide(
            item,
            isPositionTopNav,
            closeHeaderSlider,
            isMultipleSlide,
            cartItemsValue,
            isAddCouponHomepageEnabled,
            hasMounted
          )
        );
      })}
    </StyledSlider>
  );
};

const renderMultipleSlide = (
  slider,
  isPositionTopNav,
  closeHeaderSlider,
  isMultipleSlide,
  cartItemsValue,
  isAddCouponHomepageEnabled,
  hasMounted
) => {
  const { CAROUSEL_OPTIONS } = config;

  return renderSlides(
    slider,
    isPositionTopNav,
    CAROUSEL_OPTIONS,
    closeHeaderSlider,
    isMultipleSlide,
    cartItemsValue,
    isAddCouponHomepageEnabled,
    hasMounted
  );
};

const ModuleSlider = (props) => {
  const {
    slider,
    identifier,
    className,
    cookieID,
    cartItemsValue,
    isAddCouponHomepageEnabled,
    sliderVariation,
    sliderMarqueeSpeed,
  } = props;
  const isLeftSlide = sliderVariation === MARQUEE_LEFT_SLIDE;
  const isRightSlide = sliderVariation === MARQUEE_RIGHT_SLIDE;
  const isMarqueeVariation = isLeftSlide || isRightSlide;
  const cookieName = `${cookieID}_${getUUID('WC_USERACTIVITY_')}`;
  const cookieValFromHead = !isServer() ? window.slider30CookiePresent : false;
  const [bannerClosed, setBannerClosed] = useState(cookieValFromHead);
  const hasMounted = useHasMounted();
  const isPositionTopNav = identifier === SLIDER_POSITION_TOP_NAV;
  const headerClassName = isPositionTopNav
    ? 'header-topnav-slider'
    : 'header-subnav-slider content-wrapper';
  const isMultipleSlide = slider.length > 1;

  useEffect(() => {
    if (window.slider30CookiePresent && !bannerClosed) {
      setBannerClosed(true);
    }
  }, [bannerClosed]);

  const closeHeaderSlider = () => {
    const currentDate = getDateObject();
    setCookie({
      key: cookieName,
      value: currentDate.toGMTString(),
      daysAlive: 10,
    });
    if (isClient()) window.slider30CookiePresent = true;
    setBannerClosed(true);
  };

  if (isMarqueeVariation) {
    return !bannerClosed ? (
      <StyledMarqueeContainer
        className={`${className} ${headerClassName} module-slider-marquee-variation-container`}
      >
        <MarqueeSlider
          slider={slider}
          animationSpeed={parseInt(sliderMarqueeSpeed, 10)}
          slideVariation={sliderVariation}
          isRightSlideAnimation={isRightSlide}
        />
        {renderCloseButton(isPositionTopNav, closeHeaderSlider)}
      </StyledMarqueeContainer>
    ) : (
      <></>
    );
  }

  return !isMarqueeVariation && !bannerClosed ? (
    <StyledContainer className={`${className} ${headerClassName} module-slider-container`}>
      {isMultipleSlide
        ? renderMultipleSlide(
            slider,
            isPositionTopNav,
            closeHeaderSlider,
            isMultipleSlide,
            cartItemsValue,
            isAddCouponHomepageEnabled,
            hasMounted
          )
        : renderSingleSlide(
            slider[0],
            isPositionTopNav,
            closeHeaderSlider,
            isMultipleSlide,
            cartItemsValue,
            isAddCouponHomepageEnabled,
            hasMounted
          )}
    </StyledContainer>
  ) : (
    <></>
  );
};

ModuleSlider.propTypes = {
  slider: PropTypes.shape([
    {
      headerTextLine: PropTypes.shape([]),
      linkedImage: PropTypes.arrayOf(
        PropTypes.shape({
          color: PropTypes.shape({}),
          image: PropTypes.shape({}),
          link: PropTypes.shape({}),
        })
      ),
    },
  ]).isRequired,
  identifier: PropTypes.string.isRequired,
  className: PropTypes.string,
  cookieID: PropTypes.string,
  handleApplyCoupon: PropTypes.func,
  handleRemoveCoupon: PropTypes.func,
  couponDetails: PropTypes.shape({}),
  isAddCouponHomepageEnabled: PropTypes.bool,
  sliderVariation: PropTypes.string,
  sliderMarqueeSpeed: PropTypes.number,
};

ModuleSlider.defaultProps = {
  className: '',
  isAddCouponHomepageEnabled: false,
  sliderVariation: '',
  sliderMarqueeSpeed: 45,
};

export default withStyles(ModuleSlider, ModuleSliderStyle);
export { ModuleSlider as ModuleSliderVanilla };
